import {
  ReactNode,
  DragEvent,
  createContext,
  useContext,
  useState,
} from 'react'
import { Stack, Backdrop, useTheme, SxProps } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'

const DropOverlayContext = createContext<ReturnType<typeof useOverlay> | null>(
  null,
)

type DropZoneProps = {
  // The children that the drop zone covers
  children: ReactNode

  // Children displayed above the scrim when dragging
  overlayChildren?: ReactNode

  // Shown instead of overlayChildren while the drag holds a type this zone won't take
  rejectedOverlayChildren?: ReactNode

  onDrop: (files: FileList) => void

  isFullscreen?: boolean

  disabled?: boolean

  // If set, only files whose MIME type is in this list can be dropped (unrecognized/empty types are let through)
  acceptedMimeTypes?: string[]

  sx?: SxProps
}

export default function DropZone({
  children,
  overlayChildren,
  rejectedOverlayChildren,
  onDrop,
  isFullscreen,
  disabled,
  acceptedMimeTypes,
  sx,
}: DropZoneProps) {
  const dropOverlay = useOverlay()
  const [isDragRejected, setIsDragRejected] = useState(false)
  const theme = useTheme()

  const parentOverlay = useContext(DropOverlayContext)

  // During drag, only file kind + MIME type are available (no name/content yet)
  function isDragAccepted(e: DragEvent<HTMLDivElement>) {
    if (!acceptedMimeTypes) return true
    return Array.from(e.dataTransfer.items ?? [])
      .filter((item) => item.kind === 'file')
      .every((item) => !item.type || acceptedMimeTypes.includes(item.type))
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    if (e.dataTransfer.types.includes('undraggable') || disabled) return
    e.preventDefault() // Prevent browser from preparing to open the file

    const rejected = !isDragAccepted(e)
    if (rejected) {
      e.dataTransfer.dropEffect = 'none' // Shows a "not-allowed" cursor
    }

    // The overlay opens either way so the zone can show it won't take these files
    setIsDragRejected(rejected)
    dropOverlay.open()
  }

  function handleDragLeave() {
    setIsDragRejected(false)
    dropOverlay.close()
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    if (!dropOverlay.isOpen) return
    e.preventDefault() // Prevent browser from trying to open the file
    e.stopPropagation() // Prevent any drop zones underneath this one from receiving the event

    if (!isDragRejected) onDrop(e.dataTransfer.files)

    setIsDragRejected(false)
    dropOverlay.close()

    // If we're in a sub-dropzone, also close the parent (who doesn't get the event since we stopped propagation)
    if (parentOverlay) {
      // eslint-disable-next-line react/destructuring-assignment
      parentOverlay.close()
    }
  }

  const isFullscreenStyle = {
    bottom: 0,
    height: '100vh',
    left: 0,
    margin: 0,
    maxHeight: '100vh',
    maxWidth: '100vw',
    padding: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    width: '100vw',
  } // matching table fullscreen https://www.material-react-table.com/docs/guides/full-screen-toggle#change-z-index-of-full-screen-table

  return (
    <DropOverlayContext.Provider value={dropOverlay}>
      <Stack
        data-testid="dropzone"
        onDragStart={(e) => {
          // This is to prevent dragging table columns from triggering drop zone
          // https://stackoverflow.com/questions/11065803/determine-what-is-being-dragged-from-dragenter-dragover-events/11125533
          if ((e?.target as HTMLButtonElement)?.id === 'table-handle') {
            e.dataTransfer.setData('undraggable', '')
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          flexGrow: 1,
          minHeight: 0,
          alignSelf: 'stretch',
          height: '100%',
        }}
      >
        <Backdrop
          sx={{
            position: 'absolute',
            color: '#fff',
            zIndex: theme.zIndex.drawer + 200,
            height: '100%',
            ...(sx as object),
            ...(isFullscreen ? isFullscreenStyle : {}),
            // A deep red rather than a pale one, so the white message stays readable over
            // it. Last so it wins over any background the caller passed in via sx.
            ...(isDragRejected
              ? { backgroundColor: 'rgba(85, 18, 18, 0.65)' }
              : {}),
          }}
          open={dropOverlay.isOpen}
          onClick={dropOverlay.close}
        >
          {isDragRejected
            ? rejectedOverlayChildren ?? overlayChildren
            : overlayChildren}
        </Backdrop>
        {children}
      </Stack>
    </DropOverlayContext.Provider>
  )
}
